import { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, Headphones, Users, FileText, CreditCard,
  FileEdit, Megaphone, BarChart3, Settings, ScrollText, LogOut, Menu, X,
  Bell, Search, Shield, Sun, Moon, ExternalLink, User, Clock, Receipt, Code,
  Gift, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { Helmet } from "react-helmet";
import { BACKOFFICE_MENUS } from "@/config/backofficeMenus";
import { getBaseUrl } from "@/config/appConfig";

const BACKEND_URL = getBaseUrl();
const API = `${BACKEND_URL}/api`;

// Helper to get current domain for cookie clearing
const getCurrentDomain = () => {
  try {
    const hostname = window.location.hostname;
    // Get the base domain (e.g., seatpilot.co.uk from www.seatpilot.co.uk)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return '.' + parts.slice(-2).join('.');
    }
    return '';
  } catch {
    return '';
  }
};

// Icon mapping
const ICON_MAP = {
  LayoutDashboard, Building2, Headphones, Users, FileText, CreditCard,
  FileEdit, Megaphone, BarChart3, Settings, ScrollText, Clock, Receipt, Code,
  Gift, Target
};

// Use shared menu configuration
const MENU_ITEMS = BACKOFFICE_MENUS;

export default function BackofficeLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [brandSettings, setBrandSettings] = useState(null);
  
  // Theme state - default to light
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('backoffice_theme') || 'light';
  });

  useEffect(() => {
    validateAccess();
    fetchBrandSettings();
  }, []);
// Apply theme
  useEffect(() => {
    localStorage.setItem('backoffice_theme', theme);
  }, [theme]);

  const validateAccess = async () => {
    // CRITICAL: Clear any cached user state first to prevent data leakage
    setUser(null);
    setPermissions([]);
    
    try {
      // Validate admin access FIRST - this uses the correct session
      const accessRes = await axios.post(`${API}/backoffice/validate-access`, {}, { withCredentials: true });
      
      if (!accessRes.data.has_access) {
        // Check if role was deleted
        if (accessRes.data.role_deleted) {
          toast.error("Your role has been deleted. Please contact an administrator.");
        } else if (accessRes.data.reason === "Token has been revoked") {
          // User was logged out - clear everything
          localStorage.clear();
          sessionStorage.clear();
          toast.error("Your session has expired. Please login again.");
        } else {
          toast.error("Admin access denied");
        }
        navigate("/backoffice/login");
        return;
      }

      // Get user info from the validate-access response or from /auth/me
      const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(userRes.data);
      
      // CRITICAL: Check if is_super_admin explicitly, don't rely on empty permissions
      const isSuperAdmin = accessRes.data.is_super_admin === true;
      const userPermissions = accessRes.data.permissions || [];
      
      // Only give full access if explicitly super admin, NOT if permissions is empty
      if (isSuperAdmin) {
        // Super admin gets all permissions
        setPermissions(MENU_ITEMS.map(item => item.key));
      } else if (userPermissions.length > 0) {
        // Regular user with specific permissions
        setPermissions(userPermissions);
      } else {
        // No permissions = no access (not super admin)
        toast.error("You don't have any permissions assigned. Contact an administrator.");
        navigate("/backoffice/login");
        return;
      }
    } catch (error) {
      // Clear any stale data on error
      setUser(null);
      setPermissions([]);
      localStorage.removeItem('backoffice_user');
      toast.error("Please login to access admin panel");
      navigate("/backoffice/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandSettings = async () => {
    try {
      const res = await axios.get(`${API}/platform/settings/public`);
      setBrandSettings(res.data.brand);
    } catch (error) {
      console.error("Failed to fetch brand settings:", error);
    }
  };

  const handleLogout = async () => {
    try {
      // Logout from BOTH backoffice AND main auth
      await Promise.all([
        axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {}),
        axios.post(`${API}/backoffice/logout`, {}, { withCredentials: true }).catch(() => {})
      ]);
    } catch (error) {
      console.error("Logout API error:", error);
    }
    
    // Clear any local storage auth data
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('backoffice_user');
    localStorage.clear();
    
    // Clear cookies with all possible path/domain combinations
    const cookiesToClear = ['access_token', 'session_token', 'admin_access_token'];
    const currentDomain = getCurrentDomain();
    const domains = ['', currentDomain, window.location.hostname];
    const paths = ['/', '/backoffice', '/api'];
    
    cookiesToClear.forEach(name => {
      domains.forEach(domain => {
        paths.forEach(path => {
          const domainStr = domain ? `; domain=${domain}` : '';
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainStr}`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainStr}; secure`;
        });
      });
    });
    
    // Clear session storage
    sessionStorage.clear();
    
    toast.success("Logged out successfully");
    
    // Force full page reload to clear any cached state
    window.location.href = "/";
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Filter menu items based on permissions - MUST have explicit permissions
  const allowedMenuItems = MENU_ITEMS.filter(item => 
    permissions.includes(item.key)
  );

  // Redirect to first allowed page if current page is not allowed
  useEffect(() => {
    if (!loading && user && permissions.length > 0) {
      // Find current menu item
      const currentPath = location.pathname;
      const currentMenuItem = MENU_ITEMS.find(item => item.path === currentPath);
      
      // If current page is not in allowed items, redirect to first allowed page
      if (currentMenuItem && !permissions.includes(currentMenuItem.key)) {
        const firstAllowed = allowedMenuItems[0];
        if (firstAllowed) {
          navigate(firstAllowed.path);
          toast.info(`Redirected to ${firstAllowed.label} - you don't have access to that page`);
        }
      }
      
      // Also redirect if on /backoffice or /backoffice/dashboard but dashboard not allowed
      if ((currentPath === '/backoffice' || currentPath === '/backoffice/dashboard') && 
          !permissions.includes('dashboard') && allowedMenuItems.length > 0) {
        navigate(allowedMenuItems[0].path);
      }
    }
  }, [loading, user, permissions, location.pathname]);
// Theme classes
  const isDark = theme === 'dark';
  const themeClasses = {
    bg: isDark ? 'bg-slate-900' : 'bg-gray-50',
    header: isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    sidebar: isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textMuted: isDark ? 'text-slate-400' : 'text-gray-500',
    menuActive: isDark ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700',
    menuHover: isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
    card: isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    input: isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses.bg} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg}`}>
      {/* Prevent search engines from indexing backoffice */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <title>Backoffice | SeatPilot Admin</title>
      </Helmet>

      {/* Top Header */}
      <header className={`fixed top-0 left-0 right-0 h-16 ${themeClasses.header} border-b z-50 flex items-center px-4`}>
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 ${themeClasses.textMuted} hover:${themeClasses.text}`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop sidebar toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`hidden lg:block p-2 ${themeClasses.textMuted} hover:${themeClasses.text}`}
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2">
            {brandSettings?.logo_url ? (
              <img 
                src={`${BACKEND_URL}${brandSettings.logo_url}`} 
                alt="Logo" 
                className="h-8 w-auto"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-purple-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">SP</span>
              </div>
            )}
            <span className={`font-bold ${themeClasses.text} hidden sm:block`}>
              {brandSettings?.company_name || 'SeatPilot'}
            </span>
          </div>
        </div>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-2 sm:gap-4">
          {/* Search */}
          <div className={`hidden md:flex items-center ${themeClasses.input} rounded-lg px-3 py-1.5 border`}>
            <Search className={`w-4 h-4 ${themeClasses.textMuted}`} />
            <input
              type="text"
              placeholder="Search..."
              className={`bg-transparent border-none outline-none ${themeClasses.text} text-sm ml-2 w-40`}
            />
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg ${isDark ? 'bg-slate-700 text-amber-400' : 'bg-gray-100 text-gray-600'} hover:opacity-80`}
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {/* Switch to User Dashboard */}
          <button
            onClick={() => window.open('/dashboard', '_blank')}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
              isDark ? 'bg-violet-600/20 text-violet-400 hover:bg-violet-600/30' : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
            }`}
            title="Open User Dashboard"
          >
            <User className="w-4 h-4" />
            <span className="hidden lg:inline">User Dashboard</span>
            <ExternalLink className="w-3 h-3" />
          </button>

          {/* Notifications - Admin context only */}
          <NotificationBell isAdmin={true} context="admin" />

          {/* User menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-right hidden sm:block">
              <p className={`text-sm font-medium ${themeClasses.text}`}>{user?.name || user?.email}</p>
              <p className={`text-xs ${themeClasses.textMuted}`}>{user?.admin_role || user?.role || "Admin"}</p>
            </div>
            <button
              onClick={() => navigate('/backoffice/profile')}
              className="w-9 h-9 bg-violet-600 rounded-full flex items-center justify-center text-white font-medium hover:bg-violet-700 transition-colors"
              title="Profile Settings"
            >
              {user?.profile_image ? (
                <img src={user.profile_image} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                (user?.name || user?.email || "A")[0].toUpperCase()
              )}
            </button>
            <button
              onClick={handleLogout}
              className={`p-2 ${themeClasses.textMuted} hover:text-red-500 transition-colors`}
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-16 left-0 bottom-0 ${themeClasses.sidebar} border-r z-40 transition-all duration-300 ${
        sidebarOpen ? "w-64" : "w-20"
      } hidden lg:block`}>
        <nav className="p-4 space-y-1">
          {allowedMenuItems.map((item) => {
            const IconComponent = ICON_MAP[item.icon] || LayoutDashboard;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? themeClasses.menuActive
                    : themeClasses.menuHover
                }`}
              >
                <IconComponent className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Mobile switch to user dashboard */}
        <div className={`absolute bottom-4 left-4 right-4 ${sidebarOpen ? '' : 'hidden'}`}>
          <button
            onClick={() => window.open('/dashboard', '_blank')}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
              isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <User className="w-4 h-4" />
            User Dashboard
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className={`fixed top-0 left-0 bottom-0 w-72 ${themeClasses.sidebar} border-r flex flex-col`}>
            {/* Mobile Header with branding */}
            <div className={`h-16 flex items-center justify-between px-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">SP</span>
                </div>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>SeatPilot</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Scrollable nav area */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {allowedMenuItems.map((item) => {
                const IconComponent = ICON_MAP[item.icon] || LayoutDashboard;
                const isActive = location.pathname === item.path;
                
                return (
                  <button
                    key={item.key}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive 
                        ? themeClasses.menuActive
                        : themeClasses.menuHover
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Bottom section with User Dashboard + Sign Out */}
            <div className={`p-4 space-y-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <button
                onClick={() => window.open('/dashboard', '_blank')}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <User className="w-4 h-4" />
                User Dashboard
                <ExternalLink className="w-3 h-3" />
              </button>
              <button
                onClick={handleLogout}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isDark ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30' : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`pt-16 transition-all duration-300 ${
        sidebarOpen ? "lg:pl-64" : "lg:pl-20"
      }`}>
        <div className="p-6">
          <Outlet context={{ user, permissions, theme, setTheme, themeClasses }} />
        </div>
      </main>
    </div>
  );
}
